import React, { useState, lazy, Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import useMediaQuery from "@mui/material/useMediaQuery";
import PrivateRoute from "./PrivateRoute";
import { usePWA } from "../hooks/usePWA";
import ManualInstallModal from "../components/ManualInstallModal";
import { useAuthStatus, AuthStatus } from "../hooks/useAuthStatus";

import LandingPage from "../pages/LandingPage";

/* =================================================================
 * 1. LAZY LOADING IMPORTS: Use React.lazy for all route components
 * ================================================================= */

const SignupPage = lazy(() => import("../pages/SignupPage"));
const FeedPage = lazy(() => import("../pages/FeedPage"));
const UserProfilePage = lazy(() => import("../pages/UserProfilePage"));
const SendRequestPage = lazy(() => import("../pages/SendRequestPage"));
const ConversationsPage = lazy(() => import("../pages/ConversationsPage"));
const LinkUpHistoryPage = lazy(() => import("../pages/LinkUpHistoryPage"));
const NotificationsPage = lazy(() => import("../pages/NotificationsPage"));
const SettingsPage = lazy(() => import("../pages/SettingsPage"));
const PricingPage = lazy(() => import("../pages/PricingPage"));
const ErrorPage = lazy(() => import("../pages/ErrorPage"));
const UnknownPage = lazy(() => import("../pages/UnknownPage"));
const AboutPage = lazy(() => import("../pages/AboutPage"));
const HowItWorksPage = lazy(() => import("../pages/HowItWorksPage"));
const TermsOfServicePage = lazy(() => import("../pages/TermsOfServicePage"));
const PrivacyPolicyPage = lazy(() => import("../pages/PrivacyPolicyPage"));
const CookieUsePage = lazy(() => import("../pages/CookieUsePage"));
const UserDataDeletionPage = lazy(
  () => import("../pages/UserDataDeletionPage"),
);
const CommunityGuidelinesPage = lazy(
  () => import("../pages/CommunityGuidelinesPage"),
);
const ContactPage = lazy(() => import("../pages/ContactPage"));
const AdminDashboard = lazy(() => import("../pages/AdminDashboard"));

// This acts as a "Ghost" container so the app doesn't flicker white while lazy loading,
// but lets the Page-level spinner be the only one visible.
const GlobalLoadingFallback = () => (
  <div
    style={{
      position: "fixed",
      inset: 0,
      backgroundColor: "#fff",
      zIndex: 9999,
    }}
  />
);

// Banner styles (unchanged)
const bannerBaseStyle = `
  fixed bottom-4 left-1/2 transform -translate-x-1/2
  max-w-md w-[95vw] sm:w-[90vw]
  rounded-xl border shadow-xl px-4 py-3
  text-sm font-medium
  z-50 select-none
  flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2
  transition-opacity duration-300
`;

const closeBtnStyle = `
  text-xl font-bold cursor-pointer ml-auto
  text-gray-500 hover:text-gray-800
  select-none
`;

const AppRoutes = ({ chatClient }) => {
  const isMobile = useMediaQuery("(max-width:900px)");

  // Single source of truth for auth status
  const { status: authStatus, isAuthenticated: isUserAuthenticated } =
    useAuthStatus();

  const {
    canInstall,
    handleInstallClick,
    isAppInstalled,
    showInAppWarning,
    setShowInAppWarning,
    showIOSInstructions,
    setShowIOSInstructions,
    showManualInstructions,
    setShowManualInstructions,
    installFailed,
  } = usePWA();

  const [dismissed, setDismissed] = useState({
    inAppWarning: false,
    iosInstructions: false,
    installFailed: false,
  });

  const [fadeOut, setFadeOut] = useState({
    inAppWarning: false,
    iosInstructions: false,
    installFailed: false,
  });

  const handleClose = (banner) => {
    setFadeOut((prev) => ({ ...prev, [banner]: true }));
    setTimeout(() => {
      setDismissed((prev) => ({ ...prev, [banner]: true }));
      setFadeOut((prev) => ({ ...prev, [banner]: false }));

      // Reset the hook state so it can be triggered again
      if (banner === "inAppWarning") setShowInAppWarning(false);
      if (banner === "iosInstructions") setShowIOSInstructions(false);
    }, 300);
  };

  // Wrapper to reset dismissed state when user clicks install again
  const handleInstallClickWrapper = () => {
    setDismissed({
      inAppWarning: false,
      iosInstructions: false,
      installFailed: false,
    });
    handleInstallClick();
  };

  return (
    <>
      {/* 2. WRAP ROUTES WITH SUSPENSE */}
      <Suspense fallback={<GlobalLoadingFallback />}>
        <Routes>
          {/* Public Routes */}
          <Route
            path="/"
            element={
              <LandingPage
                showInstallButton={!isAppInstalled}
                handleInstallClick={handleInstallClickWrapper}
              />
            }
          />
          <Route path="/error" element={<ErrorPage isMobile={isMobile} />} />
          <Route path="/about" element={<AboutPage isMobile={isMobile} />} />
          <Route
            path="/how-it-works"
            element={<HowItWorksPage isMobile={isMobile} />}
          />
          <Route
            path="/terms-of-service"
            element={<TermsOfServicePage isMobile={isMobile} />}
          />
          <Route
            path="/privacy-policy"
            element={<PrivacyPolicyPage isMobile={isMobile} />}
          />
          <Route
            path="/cookie-use"
            element={<CookieUsePage isMobile={isMobile} />}
          />
          <Route
            path="/data-deletion-instructions"
            element={<UserDataDeletionPage />}
          />
          <Route
            path="/community-guidelines"
            element={<CommunityGuidelinesPage isMobile={isMobile} />}
          />
          <Route
            path="/contact"
            element={<ContactPage isMobile={isMobile} />}
          />
          <Route
            path="/registration"
            element={
              isUserAuthenticated ? (
                <Navigate to="/home" replace />
              ) : (
                <SignupPage />
              )
            }
          />

          {/* Private Routes */}
          <Route element={<PrivateRoute />}>
            <Route path="/home" element={<FeedPage isMobile={isMobile} />} />
            <Route
              path="/notifications"
              element={<NotificationsPage isMobile={isMobile} />}
            />
            <Route
              path="/profile/:id"
              element={<UserProfilePage isMobile={isMobile} />}
            />
            <Route
              path="/send-request/:linkupId"
              element={<SendRequestPage isMobile={isMobile} />}
            />
            <Route
              path="/history"
              element={<LinkUpHistoryPage isMobile={isMobile} />}
            />
            <Route
              path="/history/expired"
              element={<LinkUpHistoryPage isMobile={isMobile} />}
            />
            <Route
              path="/history/requests-sent"
              element={<LinkUpHistoryPage isMobile={isMobile} />}
            />
            <Route
              path="/history/requests-received"
              element={<LinkUpHistoryPage isMobile={isMobile} />}
            />
            <Route
              path="/messages"
              element={<ConversationsPage isMobile={isMobile} />}
            />
            <Route
              path="/settings"
              element={
                <SettingsPage isMobile={isMobile} chatClient={chatClient} />
              }
            />
            <Route
              path="/pricing"
              element={<PricingPage isMobile={isMobile} />}
            />
            <Route
              path="/admin"
              element={
                <AdminDashboard isMobile={isMobile} chatClient={chatClient} />
              }
            />
          </Route>

          {/* Fallback */}
          <Route
            path="*"
            element={
              <UnknownPage
                isAuthenticated={isUserAuthenticated}
                attemptedPath={window.location.pathname}
                redirectTo={isUserAuthenticated ? "/home" : "/"}
              />
            }
          />
        </Routes>
      </Suspense>

      {/* PWA Banners */}
      {showInAppWarning && !dismissed.inAppWarning && (
        <div
          className={`${bannerBaseStyle} bg-yellow-100 text-yellow-900 border-yellow-400 ${
            fadeOut.inAppWarning ? "opacity-0" : "opacity-100"
          }`}
          role="alert"
        >
          <p className="text-left leading-snug">
            ⚠️ You’re using an in-app browser (e.g., Messenger, Instagram).
            <br />
            Tap <strong>•••</strong> or the{" "}
            <svg
              className="inline w-4 h-4"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path d="M12 16c-.6 0-1-.4-1-1V6.4l-2.3 2.3c-.4.4-1 .4-1.4 0s-.4-1 0-1.4l4-4c.4-.4 1-.4 1.4 0l4 4c.4.4.4 1 0 1.4s-1 .4-1.4 0L13 6.4V15c0 .6-.4 1-1 1zM20 22H4c-1.1 0-2-.9-2-2V10c0-.6.4-1 1-1s1 .4 1 1v10h16V10c0-.6.4-1 1-1s1 .4 1 1v10c0 1.1-.9 2-2 2z" />
            </svg>{" "}
            Share icon, then choose{" "}
            <strong>‘Open in Safari’ or ‘Open in Chrome’</strong>.
          </p>
          <button
            onClick={() => handleClose("inAppWarning")}
            className={closeBtnStyle}
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      {/* iOS Instructions */}
      {showIOSInstructions && !dismissed.iosInstructions && (
        <div
          className={`${bannerBaseStyle} bg-blue-100 text-blue-900 border-blue-400 ${
            fadeOut.iosInstructions ? "opacity-0" : "opacity-100"
          }`}
          role="alert"
        >
          <div className="flex flex-col gap-2 text-left leading-snug w-full">
            📲 <strong>iOS Installation Guide:</strong>
            <ol className="list-decimal pl-5 space-y-1">
              {/* Remove flex from the li to preserve the list marker */}
              <li className="pl-1">
                <span>
                  Tap the{" "}
                  <svg
                    className="inline-block w-4 h-4 mb-1"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    style={{ verticalAlign: "middle" }}
                  >
                    <path d="M12 16c-.6 0-1-.4-1-1V6.4l-2.3 2.3c-.4.4-1 .4-1.4 0s-.4-1 0-1.4l4-4c.4-.4 1-.4 1.4 0l4 4c.4.4.4 1 0 1.4s-1 .4-1.4 0L13 6.4V15c0 .6-.4 1-1 1zM20 22H4c-1.1 0-2-.9-2-2V10c0-.6.4-1 1-1s1 .4 1 1v10h16V10c0-.6.4-1 1-1s1 .4 1 1v10c0 1.1-.9 2-2 2z" />
                  </svg>{" "}
                  <strong>Share</strong> button (may be inside the{" "}
                  <strong>"..."</strong> menu).
                </span>
              </li>
              <li>
                Scroll and tap <strong>‘Add to Home Screen’</strong>.
              </li>
              <li>
                Tap <strong>‘Add’</strong> in the top-right corner.
              </li>
            </ol>
          </div>
          <button
            onClick={() => handleClose("iosInstructions")}
            className={closeBtnStyle}
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      {/* Install Failed */}
      {installFailed && !dismissed.installFailed && (
        <div
          className={`${bannerBaseStyle} bg-gray-100 text-gray-800 border-gray-300 ${
            fadeOut.installFailed ? "opacity-0" : "opacity-100"
          }`}
          role="alert"
        >
          <p className="text-left leading-snug">
            ℹ️ Installation is not available right now.
            <br />
            Make sure you’re not in Incognito mode or try refreshing the page.
          </p>
          <button
            onClick={() => handleClose("installFailed")}
            className={closeBtnStyle}
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      {/* Manual Install Instructions Modal */}
      <ManualInstallModal
        open={showManualInstructions}
        onClose={() => setShowManualInstructions(false)}
      />
    </>
  );
};

export default AppRoutes;
